import { useCallback } from 'react'
import { CommandCenter } from '../features/live-ops/components/CommandCenter'
import { DraftReviewStrip } from '../features/live-ops/components/DraftReviewStrip'
import { LiveFeed } from '../features/live-ops/components/LiveFeed'
import { CriticalAlerts } from '../features/live-ops/components/CriticalAlerts'
import { IncidentsList } from '../features/live-ops/components/IncidentsList'
import { useLiveOps } from '../features/live-ops/hooks/useLiveOps'
import { useUpdateIncidentStatus } from '../features/incidents/hooks/useUpdateIncidentStatus'
import { LoadErrorState } from '../components/LoadErrorState'

export function LiveOpsPage(): JSX.Element {
  const { data, isLoading, isError, refetch } = useLiveOps()
  const updateStatus = useUpdateIncidentStatus()

  const handleAck = useCallback(
    (id: number) => updateStatus.mutate({ id, body: { status: 'ACKNOWLEDGE' } }),
    [updateStatus],
  )
  const handleKeep = useCallback(
    (id: number) => updateStatus.mutate({ id, body: { status: 'NEW' } }),
    [updateStatus],
  )

  if (isLoading) {
    return (
      <div data-testid="live-ops-loading" className="flex h-full flex-col gap-6 overflow-y-auto">
        <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-xl bg-gray-100" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <LoadErrorState message="Couldn't load live operations" onRetry={() => refetch()} />
    )
  }

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto">
      <CommandCenter stats={data.kpis} />

      <DraftReviewStrip drafts={data.draftTasks} onKeep={handleKeep} isUpdating={updateStatus.isPending} />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="min-w-0 flex-1">
          <LiveFeed incidents={data.securityTasks} />
        </div>
        <CriticalAlerts alerts={data.alerts} onAck={handleAck} isUpdating={updateStatus.isPending} />
      </div>

      <IncidentsList incidents={data.incidentsToday} />
    </div>
  )
}
